/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

export const GUIDED_LEARNING_CONTRACT_ID = 'QP-LEARN-001' as const;
export const GUIDED_LEARNING_SCHEMA_VERSION = 1 as const;

export const GUIDED_LEARNING_LOCALES = [
  'tr',
  'en',
  'ar',
  'de',
  'fr',
  'es',
  'it',
  'ru',
  'zh',
  'ja',
] as const;

export const GUIDED_LEARNING_CAPABILITIES = [
  'FOCUS',
  'SET_VALUE',
  'SELECT',
  'ACTIVATE',
] as const;

export const GUIDED_LEARNING_STEP_KINDS = [
  'NAVIGATE',
  'EXPLAIN',
  'INPUT',
  'SELECT',
  'ACTIVATE',
  'CHECKPOINT',
] as const;

export const GUIDED_LEARNING_MODES = ['DEMO', 'COACH', 'CHALLENGE'] as const;

export type GuidedLearningLocale = (typeof GUIDED_LEARNING_LOCALES)[number];
export type GuidedLearningCapability = (typeof GUIDED_LEARNING_CAPABILITIES)[number];
export type GuidedLearningStepKind = (typeof GUIDED_LEARNING_STEP_KINDS)[number];
export type GuidedLearningMode = (typeof GUIDED_LEARNING_MODES)[number];

export interface GuidedLearningRouteContract {
  key: string;
  pathPattern: string;
  version: number;
}

export interface GuidedLearningTargetContract {
  key: string;
  capabilities: GuidedLearningCapability[];
  version: number;
  fieldType?: string;
  optionKeys?: string[];
}

export interface GuidedLearningFixtureContract {
  key: string;
  version: number;
  dependencies: string[];
  roleProfileCode: string;
}

export interface GuidedLearningCheckpointContract {
  key: string;
  version: number;
  required: boolean;
  weight: number;
}

export interface GuidedLearningProductManifest {
  schemaVersion: typeof GUIDED_LEARNING_SCHEMA_VERSION;
  contractId: typeof GUIDED_LEARNING_CONTRACT_ID;
  applicationCode: string;
  applicationVersion: string;
  locales: GuidedLearningLocale[];
  routes: GuidedLearningRouteContract[];
  targets: GuidedLearningTargetContract[];
  fixtures: GuidedLearningFixtureContract[];
  checkpoints: GuidedLearningCheckpointContract[];
  forbiddenOutboundCapabilities: string[];
  goldenScenarioKeys: string[];
}

export interface GuidedLearningManifestEnvelope {
  manifest: GuidedLearningProductManifest;
  digest: string;
}

export interface GuidedLearningSeedCommand {
  workspaceRef: string;
  fixtureKey: string;
  fixtureVersion: number;
  scenarioDigest: string;
}

export interface GuidedLearningCheckpointCommand {
  workspaceRef: string;
  scenarioDigest: string;
}

export interface GuidedLearningEvictCommand {
  workspaceRef: string;
  reasonCode: 'RUN_FINISHED' | 'LEASE_EXPIRED' | 'CLEANUP_RETRY' | 'OPERATOR_REQUEST';
}

export interface GuidedLearningOperationContext<TCommand> {
  command: TCommand;
  customerId: bigint;
  companyId?: bigint;
  actorUserId?: bigint;
  prisma: unknown;
}

export interface GuidedLearningOperationResult {
  status: 'COMPLETED' | 'ALREADY_COMPLETED' | 'FAILED';
  code: string;
  evidenceDigest?: string;
}

export interface GuidedLearningCheckpointResult {
  passed: boolean;
  code: string;
  evidenceDigest: string;
}

export interface GuidedLearningProductAdapter {
  manifest: GuidedLearningProductManifest;
  /**
   * Training browser oturumunun ürün içindeki minimum yetki profili.
   * Manifest fixture roleProfileCode değerleri burada açıkça tanımlanır;
   * wildcard ve ürünler arası izin kabul edilmez.
   */
  roleProfiles: Record<string, string[]>;
  seed: (
    context: GuidedLearningOperationContext<GuidedLearningSeedCommand>,
  ) => Promise<GuidedLearningOperationResult>;
  verifyCheckpoint: (
    checkpointKey: string,
    context: GuidedLearningOperationContext<GuidedLearningCheckpointCommand>,
  ) => Promise<GuidedLearningCheckpointResult>;
  evict?: (
    context: GuidedLearningOperationContext<GuidedLearningEvictCommand>,
  ) => Promise<void>;
}
